import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Mail, Clock, Send, CalendarDays, X, CheckCircle, AlertCircle, Upload, Image, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  // Braiding Services
  { value: 'knotless-large', label: 'Knotless Braids - Large (₱3,500)' },
  { value: 'knotless-medium', label: 'Knotless Braids - Medium (₱4,000)' },
  { value: 'knotless-small', label: 'Knotless Braids - Small (₱5,000)' },
  { value: 'box-large', label: 'Box Braids - Large (₱3,000)' },
  { value: 'box-medium', label: 'Box Braids - Medium (₱3,500)' },
  { value: 'box-small', label: 'Box Braids - Small (₱4,000)' },
  { value: 'island-large', label: 'Island Twist - Large (₱3,500)' },
  { value: 'island-medium', label: 'Island Twist - Medium (₱4,000)' },
  { value: 'island-small', label: 'Island Twist - Small (₱4,000)' },
  { value: 'boho-medium', label: 'Boho Braids - Medium (₱4,000)' },
  { value: 'boho-small', label: 'Boho Braids - Small (₱4,500)' },
  { value: 'cornrows', label: 'Cornrows (₱1,000)' },
  { value: 'stitch-cornrows', label: 'Stitch Cornrows (₱1,500)' },
  { value: 'feed-in', label: 'Feed in Cornrows (₱1,500)' },
  { value: 'french-curls', label: 'French Curls (₱4,000)' },
  { value: 'pick-drop', label: 'Pick and Drop (₱3,000)' },
  { value: 'crochet', label: 'Crochet Braids (₱2,500)' },
  { value: 'fulani-natural', label: 'Fulani Natural (₱1,500)' },
  { value: 'fulani-ext', label: 'Fulani Extension (₱3,000)' },
  { value: 'locs-half', label: 'Locs - Half Head (₱1,500)' },
  { value: 'locs-full', label: 'Locs - Full Head (₱2,500)' },
  { value: 'sew-in', label: 'Sew in Weave (₱2,500)' },
  { value: 'frontal', label: 'Frontal Installation (₱1,500)' },
  // Hair Care
  { value: 'hair-wash', label: 'Hair Wash (₱350)' },
  { value: 'take-down', label: 'Natural Take Down (₱300)' },
  { value: 'ext-take-down', label: 'Extension Take Down (₱600)' },
  // Haircut Services
  { value: 'premium-cut', label: 'Premium Haircut Package (₱1,000)' },
  { value: 'gold-package', label: 'Gold Package (₱750)' },
  { value: 'haircut', label: 'Hair Cut (₱500)' },
  { value: 'haircut-colour', label: 'Hair Cut + Colour (₱1,000)' },
  { value: 'shave-carving', label: 'Shave / Carving (₱250)' },
  { value: 'bleach-colour', label: 'Bleaching + Colouring (₱1,500)' },
];

const timeSlots = [
  '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', 
  '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM'
];

export const Contact = () => {
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    time: '',
    message: ''
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [customer, setCustomer] = useState(null);
  const fileInputRef = useRef(null);

  // Load customer data if logged in
  useEffect(() => {
    const storedCustomer = localStorage.getItem('customer');
    if (storedCustomer) {
      const data = JSON.parse(storedCustomer);
      setCustomer(data);
      setFormData(prev => ({
        ...prev,
        name: data.name,
        email: data.email,
        phone: data.phone
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !selectedDate || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Format the date
    const bookingDate = selectedDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    // Get service label
    const serviceLabel = services.find(s => s.value === formData.service)?.label || formData.service;

    try {
      // Save booking to database
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        service_label: serviceLabel,
        date: bookingDate,
        customer_id: customer?.id || null
      });

      setBookingId(response.data.id);
      setBookingDetails({
        ...formData,
        date: bookingDate,
        serviceLabel
      });
      setShowPaymentModal(true);
      toast.success('Booking created! Please complete payment.');
    } catch (error) {
      toast.error('Failed to create booking. Please try again.');
      console.error('Booking error:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum 10MB allowed.');
        return;
      }
      setPaymentFile(file);
    }
  };

  const handleUploadPayment = async () => {
    if (!paymentFile || !bookingId) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', paymentFile);

      await axios.post(`${API}/bookings/${bookingId}/upload-payment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadComplete(true);
      toast.success('Payment screenshot uploaded! We will verify and confirm your booking.');
    } catch (error) {
      toast.error('Failed to upload screenshot. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmViaWhatsApp = () => {
    const message = `🎀 *BOOKING CONFIRMATION - BraidsByMel*

📋 *Booking ID:* ${bookingId}

👤 Name: ${bookingDetails.name}
📧 Email: ${bookingDetails.email}
📱 Phone: ${bookingDetails.phone}

💇 *Appointment:*
✨ Service: ${bookingDetails.serviceLabel}
📅 Date: ${bookingDetails.date}
🕐 Time: ${bookingDetails.time}

💰 *Payment:* ₱500 Deposit ${uploadComplete ? '✅ UPLOADED' : '- Sending screenshot now'}

${bookingDetails.message ? `📝 Notes: ${bookingDetails.message}` : ''}`;

    const whatsappUrl = `https://wa.me/639166744137?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setBookingDetails(null);
    setBookingId(null);
    setPaymentFile(null);
    setUploadComplete(false);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      time: '',
      message: ''
    });
    setSelectedDate(undefined);
  };

  return (
    <div className="page-transition pt-24">
      {/* Hero */}
      <section className="py-20 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-script text-5xl md:text-7xl text-gold mb-4"
          >
            Book Your Session
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg max-w-2xl mx-auto"
          >
            Reserve your VIP experience and step into luxury
          </motion.p>
        </div>
      </section>

      {/* Contact & Booking */}
      <section className="py-16 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-script text-4xl text-gold mb-8">Get in Touch</h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-pink" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-gold-light mb-1">Location</h3>
                    <p className="text-[#F5F5F0] font-sans">5682 Doña Carmen Street</p>
                    <p className="text-[#A3A3A3] font-sans">Poblacion, Makati City</p>
                    <p className="text-[#A3A3A3] font-sans">1210 Metro Manila, Philippines</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="fab fa-whatsapp text-xl text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-gold-light mb-1">WhatsApp</h3>
                    <a href="https://wa.me/639166744137" target="_blank" rel="noopener noreferrer" className="text-[#F5F5F0] font-sans text-lg hover:text-green-500 transition-colors">
                      09166744137
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-pink" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-gold-light mb-1">Email</h3>
                    <p className="text-[#A3A3A3] font-sans">info@braidsbymel.online</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-pink" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-gold-light mb-1">Hours</h3>
                    <p className="text-[#A3A3A3] font-sans">Mon - Sat: 11:00 AM - 10:00 PM</p>
                    <p className="text-[#A3A3A3] font-sans">Sunday: By Appointment Only</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mb-10">
                <h3 className="font-serif text-lg text-gold-light mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://facebook.com/share/18TvPyzdbo/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
                    data-testid="contact-facebook"
                  >
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a
                    href="https://tiktok.com/@braidsbymel25"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
                    data-testid="contact-tiktok"
                  >
                    <i className="fab fa-tiktok"></i>
                  </a>
                  <a
                    href="https://www.instagram.com/reel/DVghGgEE37e/?igsh=MTAxNzgxaDRmMTh4dQ=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
                    data-testid="contact-instagram"
                  >
                    <i className="fab fa-instagram"></i>
                  </a>
                </div>
              </div>

              {/* Google Maps */}
              <div>
                <h3 className="font-serif text-lg text-gold-light mb-4">Find Us</h3>
                <div className="rounded-xl overflow-hidden border border-gold/30">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3861.802!2d121.0303!3d14.5649!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397c90a4c7c7c7d%3A0x0!2sDo%C3%B1a%20Carmen%20Street%2C%20Poblacion%2C%20Makati%20City!5e0!3m2!1sen!2sph!4v1710979200000!5m2!1sen!2sph"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="BraidsByMel Location"
                    className="grayscale hover:grayscale-0 transition-all duration-500"
                  ></iframe>
                </div>
                <a
                  href="https://www.google.com/maps/search/5682+Do%C3%B1a+Carmen+Street+Poblacion+Makati+City+Philippines"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-gold hover:text-pink transition-colors font-sans text-sm"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                </a>
              </div>
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="glass rounded-2xl p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-6 h-6 text-pink" />
                    <h2 className="font-serif text-2xl text-gold-light">Book Appointment</h2>
                  </div>
                  {customer ? (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <User className="w-4 h-4" />
                      <span>Logged in as {customer.name}</span>
                    </div>
                  ) : (
                    <Link to="/account" className="text-gold hover:text-pink text-sm transition-colors">
                      Sign in for faster booking →
                    </Link>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gold font-sans">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        className="bg-[#0a0a0a] border-gold/30 text-white placeholder:text-[#666] focus:border-gold"
                        data-testid="booking-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gold font-sans">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className="bg-[#0a0a0a] border-gold/30 text-white placeholder:text-[#666] focus:border-gold"
                        data-testid="booking-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gold font-sans">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        className="bg-[#0a0a0a] border-gold/30 text-white placeholder:text-[#666] focus:border-gold"
                        data-testid="booking-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gold font-sans">Service *</Label>
                      <Select onValueChange={(value) => handleSelectChange('service', value)} value={formData.service}>
                        <SelectTrigger className="bg-[#0a0a0a] border-gold/30 text-white" data-testid="booking-service">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141210] border-gold/30">
                          {services.map(service => (
                            <SelectItem 
                              key={service.value} 
                              value={service.value}
                              className="text-white hover:bg-gold/20 focus:bg-gold/20"
                            >
                              {service.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Calendar */}
                  <div className="space-y-2">
                    <Label className="text-gold font-sans">Select Date *</Label>
                    <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gold/30 flex justify-center">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        className="text-white"
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>

                  {/* Time Slot */}
                  <div className="space-y-2">
                    <Label className="text-gold font-sans">Select Time *</Label>
                    <Select onValueChange={(value) => handleSelectChange('time', value)} value={formData.time}>
                      <SelectTrigger className="bg-[#0a0a0a] border-gold/30 text-white" data-testid="booking-time">
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141210] border-gold/30">
                        {timeSlots.map(time => (
                          <SelectItem 
                            key={time} 
                            value={time}
                            className="text-white hover:bg-gold/20 focus:bg-gold/20"
                          >
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gold font-sans">Additional Notes</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Any special requests or information..."
                      rows={4}
                      className="bg-[#0a0a0a] border-gold/30 text-white placeholder:text-[#666] focus:border-gold resize-none"
                      data-testid="booking-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gold text-black hover:bg-pink hover:text-white font-serif text-lg py-6 transition-all duration-300"
                    data-testid="booking-submit"
                  >
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Booking Request
                    </span>
                  </Button>

                  {/* Deposit Notice */}
                  <div className="mt-4 p-4 bg-pink/10 border border-pink/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-pink flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-gold font-serif font-semibold">₱500 Deposit Required</p>
                        <p className="text-[#A3A3A3] font-sans text-sm">
                          A deposit is required to confirm your booking. Payment via GCash.
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GCash Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && bookingDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-surface border border-gold/30 rounded-2xl max-w-lg w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gold/20 flex justify-between items-center">
                <h3 className="font-script text-3xl text-gold">
                  {uploadComplete ? 'Booking Submitted!' : 'Complete Your Booking'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-[#A3A3A3] hover:text-pink transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {uploadComplete ? (
                /* Success State */
                <div className="p-8 text-center">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h4 className="font-serif text-2xl text-gold-light mb-4">Payment Screenshot Uploaded!</h4>
                  <p className="text-[#A3A3A3] mb-6">
                    Your booking has been submitted and payment screenshot received. 
                    We will verify your payment and confirm your booking shortly.
                  </p>
                  <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6 text-left">
                    <p className="text-sm text-[#A3A3A3] mb-2">Booking Reference:</p>
                    <p className="text-gold font-mono text-lg">{bookingId}</p>
                  </div>
                  <div className="space-y-3">
                    <Button
                      onClick={handleConfirmViaWhatsApp}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-serif py-4"
                    >
                      <i className="fab fa-whatsapp mr-2"></i>
                      Send Confirmation on WhatsApp
                    </Button>
                    <Button
                      onClick={handleCloseModal}
                      variant="outline"
                      className="w-full border-gold text-gold hover:bg-gold/20"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                /* Payment Flow */
                <>
                  {/* Booking Summary */}
                  <div className="p-6 border-b border-gold/20">
                    <h4 className="font-serif text-lg text-gold-light mb-4">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Booking ID:</span>
                        <span className="text-gold font-mono">{bookingId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Name:</span>
                        <span className="text-white">{bookingDetails.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Service:</span>
                        <span className="text-pink font-semibold">{bookingDetails.serviceLabel}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Date:</span>
                        <span className="text-white">{bookingDetails.date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A3A3A3]">Time:</span>
                        <span className="text-white">{bookingDetails.time}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Section */}
                  <div className="p-6">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full mb-4">
                        <span className="font-bold">Pay with GCash</span>
                      </div>
                      <p className="text-gold font-serif text-2xl font-bold">₱500 Deposit</p>
                      <p className="text-[#A3A3A3] text-sm mt-1">Required to confirm booking</p>
                    </div>

                    {/* QR Code */}
                    <div className="bg-white rounded-xl p-4 mb-6">
                      <img
                        src="https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/wz68w4ym_signal-2026-03-21-093725.jpeg"
                        alt="GCash QR Code"
                        className="w-full max-w-[250px] mx-auto rounded-lg"
                      />
                    </div>

                    {/* GCash Number */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6 text-center">
                      <p className="text-[#A3A3A3] text-sm mb-1">Or send to GCash Number:</p>
                      <p className="text-gold font-mono text-2xl font-bold">0916 674 4137</p>
                      <p className="text-pink text-sm mt-1">ME****A S.</p>
                    </div>

                    {/* Upload Screenshot */}
                    <div className="mb-6">
                      <p className="text-gold font-serif font-semibold mb-3 flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload Payment Screenshot
                      </p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                          paymentFile ? 'border-green-500 bg-green-500/10' : 'border-gold/30 hover:border-gold'
                        }`}
                      >
                        {paymentFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <Image className="w-8 h-8 text-green-500" />
                            <div className="text-left">
                              <p className="text-green-400 font-semibold">{paymentFile.name}</p>
                              <p className="text-[#A3A3A3] text-sm">Click to change</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gold mx-auto mb-2" />
                            <p className="text-[#A3A3A3]">Click to upload screenshot</p>
                            <p className="text-[#666] text-xs mt-1">PNG, JPG up to 10MB</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="bg-pink/10 border border-pink/30 rounded-xl p-4 mb-6">
                      <h5 className="text-gold font-serif font-semibold mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-pink" />
                        Steps to Complete
                      </h5>
                      <ol className="text-[#A3A3A3] text-sm space-y-1 list-decimal list-inside">
                        <li>Send ₱500 to GCash number above</li>
                        <li>Take a screenshot of your payment</li>
                        <li>Upload the screenshot here</li>
                        <li>Click "Submit Payment" below</li>
                        <li className="text-pink">We'll verify and confirm your booking!</li>
                      </ol>
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleUploadPayment}
                      disabled={!paymentFile || isUploading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-serif text-lg py-6 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-payment-btn"
                    >
                      {isUploading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Submit Payment Screenshot
                        </span>
                      )}
                    </Button>

                    <p className="text-center text-[#666] text-xs mt-4">
                      Booking will be confirmed after payment verification
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Contact;
